import React, { useState } from 'react';
import { C } from '../data/constants';
import { NudgeLogo, Navbar, Footer, Card, PrimaryBtn, SecondaryBtn, TimeBadge } from './shared/UI';

export default function MarketingSite({ onStart }) {
  const [page, setPage] = useState('home');

  const nav = (p) => { setPage(p); window.scrollTo(0, 0); };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar page={page} setPage={nav} onJoinStudy={onStart} />
      <main style={{ flex: 1 }}>
        {page === 'home' && <HomePage onStart={onStart} />}
        {page === 'mission' && <MissionPage />}
        {page === 'about' && <AboutPage />}
        {page === 'disclaimers' && <DisclaimersPage />}
        {page === 'contact' && <ContactPage />}
      </main>
      <Footer />
    </div>
  );
}

// ---- HOME PAGE ----
function HomePage({ onStart }) {
  return (
    <>
      {/* HERO */}
      <section style={{ background: C.bg, padding: '72px 24px 64px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 60, flexWrap: 'wrap' }}>
          {/* Left */}
          <div style={{ flex: '1 1 400px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: C.tealLight, borderRadius: 20, padding: '5px 14px', marginBottom: 24,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#047857' }}>Active Research Study</span>
            </div>
            <h1 style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 'clamp(36px, 5vw, 52px)',
              fontWeight: 400, lineHeight: 1.15,
              color: C.navy, margin: '0 0 20px',
            }}>
              Financial coaching that{' '}
              <em style={{ color: C.teal, fontStyle: 'italic' }}>thinks</em>{' '}with you.
            </h1>
            <p style={{ fontSize: 17, color: C.body, lineHeight: 1.7, marginBottom: 32, maxWidth: 480 }}>
              Nudge is a personality-aware AI coaching tool that builds real financial capability
              through reflective conversation — not generic answers.
            </p>
            <PrimaryBtn onClick={onStart} style={{ fontSize: 15, padding: '13px 28px' }}>
              Join the Study &rarr;
            </PrimaryBtn>
          </div>

          {/* Right — Coaching card */}
          <div style={{ flex: '1 1 260px', display: 'flex', justifyContent: 'center' }}>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 14, padding: '28px 32px',
              boxShadow: '0 8px 40px rgba(15,31,51,0.10)',
              transform: 'rotate(-1.5deg)',
              maxWidth: 320, width: '100%',
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: C.muted, textTransform: 'uppercase', marginBottom: 20 }}>
                HOW NUDGE COACHES
              </p>
              {[
                { n: 1, color: C.teal, text: 'You explain your thinking first.' },
                { n: 2, color: C.navy, text: 'Nudge surfaces blind spots.' },
                { n: 3, color: C.mint, text: 'Then you get structured feedback.' },
              ].map(step => (
                <div key={step.n} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: step.color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>{step.n}</div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: '6px 0 0', lineHeight: 1.4 }}>
                    {step.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip removed */}

      {/* HOW THE STUDY WORKS */}
      <section style={{ background: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: C.navy, marginBottom: 10, fontWeight: 400 }}>
              How the Study Works
            </h2>
            <p style={{ color: C.muted, fontSize: 15 }}>Your journey from sign-up to completion</p>
          </div>

          {/* Timeline */}
          {[
            { n: '1', color: C.teal, title: 'Create Your Profile', time: '~5 minutes',
              desc: 'Create a secure account, review the study consent form, and complete a short demographic questionnaire. All responses use dropdowns — no sensitive details typed.' },
            { n: '2', color: C.navy, title: 'We Learn About You', time: '~30 minutes',
              desc: 'Complete a comprehensive assessment covering your personality traits (Big Five), financial literacy, AI comfort level, financial well-being, self-efficacy, and cognitive biases. Your results generate a personalized AI Profile Card that Nudge uses to tailor its coaching to you.' },
            { n: '3', color: C.gold, title: '4 Weeks of Coaching', time: '~30 min/week',
              desc: 'Interact with Nudge through four weekly modules: Foundations, Borrowing, Assets, and Protection. Nudge uses the Extended Rationale method — it asks you to think before it teaches. Every session is personalized to your personality.',
              extra: 'modules' },
            { n: '4', color: C.purple, title: 'Measure Your Growth', time: '~20 minutes',
              desc: 'Retake the same assessments from baseline so we can measure what changed. Plus answer questions about professional advice — and receive an actual no-obligation offer to connect with a qualified financial planner.' },
            { n: '5', color: C.mint, title: "You're Done", time: 'Paid within 2 weeks',
              desc: 'Receive $25 for each completed interaction. Some participants may be invited for follow-up interviews at 3 and 6 months ($25 each). Comparison group participants get full Nudge access immediately.' },
          ].map((step, idx) => (
            <div key={step.n} style={{ display: 'flex', gap: 24, marginBottom: idx < 4 ? 0 : 40 }}>
              {/* Left: circle + line */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 40, flexShrink: 0 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: step.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, zIndex: 1, flexShrink: 0,
                }}>
                  {step.n}
                </div>
                {idx < 4 && (
                  <div style={{ flex: 1, width: 2, background: C.border, minHeight: 40, margin: '6px 0' }} />
                )}
              </div>

              {/* Right: card */}
              <div style={{
                flex: 1, marginBottom: idx < 4 ? 16 : 0,
                borderLeft: `3px solid ${step.color}`,
                borderRadius: '0 12px 12px 0',
                border: `1px solid ${C.border}`,
                borderLeftColor: step.color, borderLeftWidth: 3,
                padding: '18px 22px',
                background: '#fff',
                marginBottom: 24,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: C.text, fontWeight: 400, margin: 0 }}>
                    {step.title}
                  </h3>
                  <TimeBadge text={step.time} color={step.color} />
                </div>
                <p style={{ fontSize: 14, color: C.body, lineHeight: 1.65, margin: 0 }}>{step.desc}</p>

                {step.extra === 'modules' && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                    {[
                      { w: 'Wk 1', t: 'Foundations', c: C.teal },
                      { w: 'Wk 2', t: 'Borrowing', c: C.coral },
                      { w: 'Wk 3', t: 'Assets', c: C.mint },
                      { w: 'Wk 4', t: 'Protection', c: C.navy },
                    ].map(m => (
                      <div key={m.w} style={{
                        borderTop: `3px solid ${m.c}`, padding: '8px 14px',
                        background: '#fafbfc', borderRadius: '0 0 8px 8px',
                        border: `1px solid ${C.border}`, borderTopColor: m.c, borderTopWidth: 3,
                        minWidth: 90, textAlign: 'center',
                      }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 2 }}>{m.w}</div>
                        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{m.t}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Privacy callout */}
          <div style={{
            background: '#f0fdfa', border: `1px solid ${C.tealLight}`,
            borderLeft: `4px solid ${C.teal}`,
            borderRadius: '0 8px 8px 0',
            padding: '18px 22px', marginBottom: 32,
          }}>
            <p style={{ margin: 0, fontSize: 14, color: C.text, lineHeight: 1.65 }}>
              <strong>Your data is protected at every step.</strong>{' '}
              All conversations pass through a 3-stage privacy pipeline that removes personal information
              before storage. We never collect bank account numbers, Social Security numbers, or login credentials.
            </p>
          </div>

          <div style={{ textAlign: 'center' }}>
            <PrimaryBtn onClick={onStart} style={{ fontSize: 15, padding: '13px 28px' }}>
              Join the Study
            </PrimaryBtn>
          </div>
        </div>
      </section>

      {/* GENERIC AI vs NUDGE */}
      <section style={{ background: C.bg, padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: C.navy, marginBottom: 10, fontWeight: 400 }}>
              Generic AI vs. Nudge
            </h2>
            <p style={{ color: C.muted, fontSize: 15 }}>Why answers alone don't build capability</p>
          </div>

          <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* Generic AI column */}
            <div style={{ flex: '1 1 280px', border: `1px solid ${C.border}`, borderRadius: '12px 0 0 12px', overflow: 'hidden' }}>
              <div style={{ background: C.coral, padding: '14px 20px' }}>
                <p style={{ color: '#fff', fontWeight: 700, fontSize: 15, margin: 0 }}>Generic AI / Chatbots</p>
              </div>
              <div style={{ padding: '20px' }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: C.muted, textTransform: 'uppercase', marginBottom: 14 }}>HOW IT WORKS</p>
                {['User asks a question', 'AI searches its model', 'AI gives a direct answer', 'User moves on'].map((s, i) => (
                  <div key={i} style={{ background: '#fee2e2', borderRadius: 20, padding: '7px 14px', marginBottom: 8 }}>
                    <span style={{ color: C.coral, fontSize: 13 }}><strong>{i + 1}.</strong> {s}</span>
                  </div>
                ))}
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: C.muted, textTransform: 'uppercase', margin: '20px 0 14px' }}>THE RESULT</p>
                {['No reflection or critical thinking', 'No awareness of personality', 'Bypasses cognition entirely', 'Creates illusion of competence'].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ color: C.coral, fontWeight: 700, fontSize: 16, lineHeight: 1 }}>&#10007;</span>
                    <span style={{ fontSize: 13, color: C.body, lineHeight: 1.5 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* VS divider */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', flexShrink: 0, zIndex: 2 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: C.navy, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
              }}>VS</div>
            </div>

            {/* Nudge column */}
            <div style={{ flex: '1 1 280px', border: `1px solid ${C.border}`, borderRadius: '0 12px 12px 0', overflow: 'hidden' }}>
              <div style={{ background: C.teal, padding: '14px 20px' }}>
                <p style={{ color: '#fff', fontWeight: 700, fontSize: 15, margin: 0 }}>Nudge (Extended Rationale)</p>
              </div>
              <div style={{ padding: '20px' }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: C.muted, textTransform: 'uppercase', marginBottom: 14 }}>HOW IT WORKS</p>
                {[
                  'User asks a question',
                  'Nudge asks: "What\'s YOUR reasoning?"',
                  'User reflects and explains thinking',
                  'Nudge surfaces biases with follow-ups',
                  'Nudge provides structured feedback',
                ].map((s, i) => (
                  <div key={i} style={{
                    background: i % 2 === 0 ? '#dcfce7' : '#d1fae5',
                    borderRadius: 20, padding: '7px 14px', marginBottom: 8,
                  }}>
                    <span style={{ color: '#065f46', fontSize: 13 }}><strong>{i + 1}.</strong> {s}</span>
                  </div>
                ))}
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: C.muted, textTransform: 'uppercase', margin: '20px 0 14px' }}>THE RESULT</p>
                {[
                  'Builds real understanding through friction',
                  'Adapts tone to personality profile',
                  'Forces cognitive engagement',
                  'Develops capability, not just answers',
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ color: C.teal, fontWeight: 700, fontSize: 16, lineHeight: 1 }}>&#10003;</span>
                    <span style={{ fontSize: 13, color: C.body, lineHeight: 1.5 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT MAKES NUDGE DIFFERENT */}
      <section style={{ background: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: C.navy, fontWeight: 400, marginBottom: 10 }}>
              What Makes Nudge Different
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {[
              { abbr: 'PA', bg: C.tealLight, title: 'Personality-Aware', desc: 'Adapts coaching based on your Big Five personality profile — extraversion, conscientiousness, neuroticism, and more.' },
              { abbr: 'ER', bg: '#fef9c3', title: 'Extended Rationale', desc: 'Forces reflection before teaching. Nudge never gives an answer without first asking for your reasoning.' },
              { abbr: 'RC', bg: '#ede9fe', title: 'Builds Real Capability', desc: 'Measures actual bias reduction and knowledge gains across 7 validated psychometric instruments.' },
              { abbr: 'BR', bg: '#fee2e2', title: 'Bridge to Human Advice', desc: 'Designed to prepare people for professional planners — not replace them. Expands the reach of human advice.' },
              { abbr: 'PD', bg: C.tealLight, title: 'Privacy by Design', desc: 'All conversations pass through a 3-stage PII scrubbing pipeline. No sensitive data is ever stored.' },
              { abbr: 'RB', bg: '#dcfce7', title: 'Research-Backed', desc: 'Built on peer-reviewed psychometric instruments and tested within an IRB-approved academic research framework.' },
            ].map((card, i) => (
              <div key={i} className="feature-card" style={{
                background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 12, padding: 28, cursor: 'default',
                position: 'relative', overflow: 'hidden',
              }}>
                <div className="feature-card-top-line" style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 3, background: C.teal,
                }} />
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16,
                }}>
                  {card.abbr}
                </div>
                <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: C.text, fontWeight: 400, marginBottom: 10 }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: 14, color: C.body, lineHeight: 1.65, margin: 0 }}>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ---- MISSION PAGE ----
function MissionPage() {
  return (
    <section style={{ padding: '72px 24px', background: C.bg }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, color: C.navy, fontWeight: 400, marginBottom: 10 }}>Our Mission</h1>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 40 }}>Closing the advice gap with personality-aware AI</p>

        <p style={{ fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 24 }}>
          The most effective intervention for improving financial decision-making is a human financial planner.
          But cost barriers exclude millions of households from this standard of care. This is the advice gap.
        </p>
        <p style={{ fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 24 }}>
          In the absence of professional guidance, people turn to generic AI tools. These tools give answers
          without making you think — creating an illusion of competence where biases remain untouched.
        </p>

        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: C.navy, fontWeight: 400, margin: '40px 0 16px' }}>What We Believe</h2>
        <p style={{ fontSize: 16, color: C.body, lineHeight: 1.8, marginBottom: 24 }}>
          The missing piece in financial AI is not more information — it's more reflection. Nudge knows who you
          are before it starts teaching. It reads your personality profile and calibrates its coaching to fit you.
          Then it uses the Extended Rationale framework to make you think before it teaches.
        </p>

        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: C.navy, fontWeight: 400, margin: '40px 0 16px' }}>The Bridge, Not the Destination</h2>
        <p style={{ fontSize: 16, color: C.body, lineHeight: 1.8 }}>
          Nudge is not designed to replace financial planners. It's designed to prepare people to get more
          out of them. A scalable, low-cost tool that doesn't compete with the profession but expands its
          reach by bringing more people to the table.
        </p>
      </div>
    </section>
  );
}

// ---- ABOUT PAGE ----
function AboutPage() {
  return (
    <section style={{ padding: '72px 24px', background: C.bg }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, color: C.navy, fontWeight: 400, marginBottom: 10 }}>About Us</h1>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 48 }}>The team behind Nudge</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {[
            { init: 'HV', name: 'Harry Vadalkar', role: 'Co-Investigator', bio: '', c: C.teal },
            { init: 'MM', name: 'Marc Milic', role: 'Co-Investigator', bio: '', c: C.navy },
          ].map(p => (
            <div key={p.name} style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: 28,
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%',
                background: p.c, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, marginBottom: 16,
                fontFamily: "'DM Serif Display', serif",
              }}>{p.init}</div>
              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: C.text, fontWeight: 400, marginBottom: 4 }}>{p.name}</h3>
              <p style={{ fontSize: 12, color: C.teal, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 0 }}>{p.role}</p>
              {p.bio && <p style={{ fontSize: 14, color: C.body, lineHeight: 1.7, margin: '12px 0 0' }}>{p.bio}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- DISCLAIMERS PAGE ----
function DisclaimersPage() {
  const sections = [
    { t: 'Not Financial Advice', p: 'Nudge is a research tool for educational purposes only. It does not provide personalized financial advice, investment recommendations, or tax guidance. Consult a qualified professional before making any financial decisions.' },
    { t: 'Research Limitations', p: 'This is a pilot study using a quasi-experimental design. Results support evidence of preliminary effectiveness and association but do not permit strict causal claims.' },
    { t: 'AI Limitations', p: 'Nudge is powered by AI. While designed with guardrails, responses may occasionally contain inaccuracies. Nudge is a coaching tool that helps users reflect — not a substitute for professional planning.' },
    { t: 'Data and Privacy', p: 'All data is de-identified through a 3-stage PII scrubbing pipeline. No bank accounts, SSN, or credentials are collected. Income and debt information uses categorical ranges only.' },
    { t: 'Compensation', p: 'Participants receive $25 per interaction. Each survey and interview counts as one interaction.' },
    { t: 'IRB Oversight', p: 'This study will be submitted for IRB review prior to recruitment. Participation is voluntary and may be withdrawn at any time without penalty.' },
  ];
  return (
    <section style={{ padding: '72px 24px', background: C.bg }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, color: C.navy, fontWeight: 400, marginBottom: 40 }}>Disclaimers</h1>
        {sections.map(s => (
          <div key={s.t} style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: C.text, fontWeight: 400, marginBottom: 12 }}>{s.t}</h2>
            <p style={{ fontSize: 15, color: C.body, lineHeight: 1.75, margin: 0 }}>{s.p}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- CONTACT PAGE ----
function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <section style={{ padding: '72px 24px', background: C.bg }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, color: C.navy, fontWeight: 400, marginBottom: 10 }}>Contact Us</h1>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 40 }}>Questions about the study or Nudge? Reach out.</p>

        {!sent ? (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32 }}>
            <form onSubmit={handleSubmit}>
              {['Name', 'Email', 'Subject'].map(field => (
                <div key={field} style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>{field}</label>
                  <input
                    type={field === 'Email' ? 'email' : 'text'}
                    value={form[field.toLowerCase()]}
                    onChange={e => setForm({ ...form, [field.toLowerCase()]: e.target.value })}
                    required
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      fontSize: 14, color: C.text, outline: 'none',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                </div>
              ))}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>Message</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  required
                  rows={5}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    fontSize: 14, color: C.text, outline: 'none', resize: 'vertical',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                />
              </div>
              <PrimaryBtn style={{ width: '100%', padding: '12px' }} onClick={undefined}>
                Send Message
              </PrimaryBtn>
            </form>
          </div>
        ) : (
          <div style={{
            background: C.tealLight, border: `1px solid #a7f3d0`,
            borderRadius: 12, padding: 32, textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 16, color: C.teal }}>&#10003;</div>
            <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: C.navy, fontWeight: 400, marginBottom: 10 }}>Message Sent</h3>
            <p style={{ color: C.body, fontSize: 15 }}>Thank you for reaching out. We'll get back to you shortly.</p>
          </div>
        )}
      </div>
    </section>
  );
}
